import {
  ChangeEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import { Table, createDataSource, useDatasource } from "components/Bable"
import { Tax, TaxConfigScope } from "models/taxes"
import {
  activateTax,
  deactivateTax,
  deleteTax,
  fetchSemantics,
  fetchTaxes,
  patchTax,
} from "api/taxes"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import { AddNewTax } from "./AddNewTax"
import { AddRoundingLimit } from "./addRoundingLimit"
import { ApiError } from "api/http/utils"
import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import { COLUMNS } from "./columns"
import { EmptyState } from "components/EmptyState"
import { Search } from "../components/Search"
import Surface from "components/Surface"
import { TaxExempt } from "./TaxExempt"
import { ReactComponent as TaxesIcon } from "assets/icon-tax.svg"
import { fetchCurrentMarket } from "api/markets"
import { getCurrencyNameFromCurrencyCode } from "utils/currencies"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

const getRowId = (x: Tax) => x.id

export const globalState = createDataSource({ getRowId })

type Props = {
  queryKeyParam: string
  configScope?: TaxConfigScope
}

const Taxes = (props: Props) => {
  const { t } = useI18n()
  const { meta, hasAuthority } = useAuthenticated()
  const id = meta?.layerId

  const { configScope, queryKeyParam } = props
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [searchValue, setSearchValue] = useState<string>("")
  const [open, setOpen] = useState<boolean>(false)
  const isModalOpen = Boolean(modalInfo)
  const actionAllowed = hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
  const feedback = useSendFeedBack()

  useEffect(() => globalState.setErrors({}), [])

  // ** fetch taxes
  const { data: taxes, isLoading } = useQuery(
    [`${queryKeyParam}-taxes`, configScope, id],
    fetchTaxes,
    {
      onSuccess: (d: Tax[]) => {
        globalState.setData(d)
      },
    },
  )

  const [activate] = useMutation(activateTax, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries([`${queryKeyParam}-taxes`, configScope, id])
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const [deactivate] = useMutation(deactivateTax, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries([`${queryKeyParam}-taxes`, configScope, id])
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  // ** fetch current market
  const { data: market } = useQuery(["current-market"], fetchCurrentMarket)

  // ** fetch semantics
  const { data: taxSemantics } = useQuery("taxes-semantics", fetchSemantics)

  // ** patch tax inputs
  const [patch] = useMutation(patchTax, {
    onError: (e: ApiError, mf: Partial<Tax>) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!mf.id && globalState.setRowErrors(mf.id, errorMap)
      queryCache.invalidateQueries([`${queryKeyParam}-taxes`, configScope, id])
      feedback(t(e.message), "error")
    },
  })

  // ** remove tax
  const [remove] = useMutation(deleteTax, {
    onSuccess: (_, id: string) => {
      setModalInfo(undefined)
      globalState.removeRow(id)
      queryCache.invalidateQueries(`${queryKeyParam}-taxes`)
    },
    onError: () => setModalInfo(undefined),
  })

  const source = useDatasource({
    state: globalState,
    filters: [
      {
        active: !!searchValue.trim(),
        predicate: (row) => {
          const reg = new RegExp(searchValue, "ig")
          return reg.test(row?.label || "")
        },
      },
    ],
  })

  const handleSearchChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setSearchValue(() => value)
  }

  const handleSearchClear = () => {
    setSearchValue(() => "")
  }

  const onUpdate = useCallback(
    async (row, label, value) => {
      patch({ id: row.id, [label]: value })
    },
    [patch],
  )

  const handleActivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("admin.taxes.activate.title"),
        description: t("admin.taxes.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await activate(id),
      })
    },
    [activate, t],
  )

  const handleDeactivate = useCallback(
    (id: string) => {
      setModalInfo({
        type: "primary_destructive",
        title: t("admin.taxes.deactivate.title"),
        description: t("admin.taxes.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await deactivate(id),
      })
    },
    [deactivate, t],
  )

  const handleDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("admin.taxes.delete.title"),
        type: "primary_destructive",
        description: t("admin.taxes.delete.description"),
        submitLabel: t("admin.taxes.delete.action"),
        onSubmit: async () => await remove(id),
      })
    },
    [remove, t],
  )

  const handleClick = () => setOpen(true)

  const onClose = () => setOpen(false)

  const taxesSemanticsMapped =
    taxSemantics?.map((vp) => ({
      name: vp?.name,
      value: vp?.value,
    })) || []

  const context = {
    taxesSemanticsOptions: taxesSemanticsMapped,
    onUpdate,
    onDelete: handleDelete,
    onActivate: handleActivate,
    onDeactivate: handleDeactivate,
  }

  return (
    <contextOptions.Provider value={context}>
      <Container>
        <Breadcrumbs section={t("nav.admin")} current={t("nav.market.taxes")} />
        <Header label={t("nav.market.taxes")} />
        {!!market?.id ? (
          <TaxExempt marketId={market.id} marketTaxExempt={market?.taxExempt} />
        ) : null}
        <AddRoundingLimit rounding={market?.rounding} id={market?.id} />
        <Surface className={styles.surface} mb={2}>
          <h5 className={cx(styles.header, "BodyBody-1BlackBold")}>
            {t("market.taxes.header")}
          </h5>
          <div className={styles.tableToolbar}>
            <Search
              name={"currency"}
              value={getCurrencyNameFromCurrencyCode(market?.currencyCode)}
              handleChange={handleSearchChange}
              handleClear={handleSearchClear}
              disabled
            />
            {hasAuthority(["AUTH_CREATE_TAX"]) && (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClick}
                  disabled={open}
                >
                  {t("admin.taxes.create_button")}
                </Button>
              </div>
            )}
          </div>
          {open ? (
            <AddNewTax
              onClose={onClose}
              isLoading={false}
              data={taxes}
              configOwnerId={id}
              {...props}
            />
          ) : null}
          <Table<Tax>
            columns={COLUMNS(actionAllowed)}
            source={source}
            height={650}
            className={styles.table}
            pending={isLoading}
            emptyText={
              <EmptyState
                icon={<TaxesIcon />}
                title={t("market-finance.financial.tax.title")}
                subtitle={
                  hasAuthority(["AUTH_CREATE_TAX"])
                    ? t("market-finance.financial.tax.subtitle")
                    : undefined
                }
              >
                {hasAuthority(["AUTH_CREATE_TAX"]) ? (
                  <span
                    onClick={handleClick}
                    className={cx(
                      "BodyBody-1Primary-mainRegular",
                      styles.createNew,
                    )}
                  >
                    {t("market-finance.financial.tax.add_new")}
                  </span>
                ) : null}
              </EmptyState>
            }
          />
          <ConfirmationModal
            open={isModalOpen}
            onCancel={() => setModalInfo(undefined)}
            {...modalInfo}
          />
        </Surface>
      </Container>
    </contextOptions.Provider>
  )
}

export default Taxes

const contextOptions = createContext<
  | {
      taxesSemanticsOptions:
        | {
            name: string
            value: string
          }[]
        | undefined
      onUpdate: (
        row: Tax,
        label: string,
        value?: string | number | string[] | boolean,
      ) => Promise<void>
      onDelete: (id: string) => void
      onActivate: (id: string) => void
      onDeactivate: (id: string) => void
    }
  | undefined
>(undefined)

export const useContextOptions = () => {
  const options = useContext(contextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  tableToolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 var(--spacing-xxl-2);

    .left {
      display: flex;
      align-items: center;
      & > :not(:first-child) {
        margin-left: var(--spacing-m);
      }
    }
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
  surface: css`
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
  `,
  header: css`
    padding: var(--spacing-m) 0;
  `,
}
